// ============================================================================
// Credential Rotation Dialog
// ============================================================================

import { useState } from 'react'
import { Loader2, KeyRound } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useRotateCredentials } from '@/hooks/useIntegrations'

interface CredentialRotationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  credentialSetId: string
  credentialLabel: string
}

export function CredentialRotationDialog({
  open,
  onOpenChange,
  credentialSetId,
  credentialLabel,
}: CredentialRotationDialogProps) {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const rotate = useRotateCredentials()

  const handleRotate = async () => {
    await rotate.mutateAsync({ credentialSetId, newPassword })
    setNewPassword('')
    setConfirmPassword('')
    onOpenChange(false)
  }

  const passwordsMatch = newPassword === confirmPassword
  const canSubmit = newPassword.length > 0 && passwordsMatch && !rotate.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Rotate Credentials
          </DialogTitle>
          <DialogDescription>
            Update the password for "{credentialLabel}". The new credentials will be
            tested before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <Label>New Password / API Key</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
          </div>
          <div>
            <Label>Confirm Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-destructive mt-1">Passwords do not match</p>
            )}
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={handleRotate}>
              {rotate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Test & Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
